<template>
  <div class="bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl p-4">
    <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Date</p>

    <div class="flex gap-2 mb-4">
      <button
        @click="selectPreset('upcoming')"
        class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border transition-colors"
        :class="preset === 'upcoming'
          ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
          : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'"
      >
        <svg v-if="preset === 'upcoming'" class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
        </svg>
        Upcoming bookings
      </button>
      <button
        @click="selectPreset('past')"
        class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border transition-colors"
        :class="preset === 'past'
          ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
          : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'"
      >
        <svg v-if="preset === 'past'" class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
        </svg>
        Past bookings
      </button>
    </div>

    <div class="flex items-center gap-3 mb-2 text-xs">
      <span class="text-gray-400 dark:text-gray-500">From</span>
      <span class="font-medium text-gray-700 dark:text-gray-200">{{ fromDate || 'Any' }}</span>
      <span class="text-gray-300 dark:text-gray-600">–</span>
      <span class="text-gray-400 dark:text-gray-500">To</span>
      <span class="font-medium text-gray-700 dark:text-gray-200">{{ toDate || 'Any' }}</span>
    </div>

    <div class="mb-4 flex justify-center">
      <v-date-picker
        v-model="range"
        :is-range="true"
        class="search-date-picker"
        :is-dark="isDark"
        title-position="left"
      />
    </div>

    <div class="flex items-center justify-end gap-4">
      <button @click="reset" class="text-sm text-gray-500 dark:text-gray-400 hover:underline">
        Reset
      </button>
      <button
        @click="apply"
        class="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
      >
        Apply
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  preset: { type: String, default: null }, // 'upcoming' | 'past' | null
  from:   { type: String, default: '' },    // 'YYYY-MM-DD'
  to:     { type: String, default: '' },    // 'YYYY-MM-DD'
})

const emit = defineEmits(['apply', 'reset'])

const isDark = ref(document.documentElement.classList.contains('dark'))

const preset   = ref(props.preset)
const fromDate = ref(props.from)
const toDate   = ref(props.to)

function toISODate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function fromISODate(value) {
  if (!value) return null
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y, m - 1, d)
}

// v-calendar's range mode works off a single { start, end } model rather
// than two independent dates, so from/to are bridged through this computed.
const range = computed({
  get: () => ({ start: fromISODate(fromDate.value), end: fromISODate(toDate.value) }),
  set: (value) => {
    preset.value = null
    fromDate.value = value?.start ? toISODate(value.start) : ''
    toDate.value = value?.end ? toISODate(value.end) : ''
  },
})

function todayISO() {
  return toISODate(new Date())
}

function selectPreset(value) {
  preset.value = preset.value === value ? null : value
  if (preset.value === 'upcoming') {
    fromDate.value = todayISO()
    toDate.value = ''
  } else if (preset.value === 'past') {
    fromDate.value = ''
    toDate.value = todayISO()
  } else {
    fromDate.value = ''
    toDate.value = ''
  }
}

function apply() {
  emit('apply', { preset: preset.value, from: fromDate.value, to: toDate.value })
}

function reset() {
  preset.value = null
  fromDate.value = ''
  toDate.value = ''
  emit('reset')
}
</script>

<style scoped>
/* Restyle v-calendar to match our design system (see availability_calendar.css
   for the same pattern applied to the bigger availability-page calendar). */
:deep(.search-date-picker.vc-container) {
  --day-width: 36px;
  --day-height: 36px;
  border: none;
  background: transparent;
  font-family: inherit;
  width: 100%;
}
:deep(.search-date-picker .vc-header) {
  padding: 0 2px 10px;
}
:deep(.search-date-picker .vc-title) {
  font-size: 13px;
  font-weight: 600;
}
:deep(.search-date-picker .vc-arrow) {
  width: 28px;
  height: 28px;
  border-radius: 9999px;
  color: #6b7280;
}
:deep(.search-date-picker .vc-arrow:hover) {
  background-color: #f3f4f6;
}
:deep(.search-date-picker .vc-weekday) {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
}
:deep(.search-date-picker .vc-day) {
  height: var(--day-height);
  min-width: var(--day-width);
}
:deep(.search-date-picker .vc-day-content) {
  font-size: 12px;
  width: 32px;
  height: 32px;
}
:deep(.search-date-picker .vc-day-content:hover) {
  background-color: #eff6ff;
  color: #2563eb;
}
:deep(.search-date-picker .vc-highlight) {
  background-color: #2563eb;
}
:deep(.search-date-picker .vc-day.is-today .vc-day-content:not(.vc-highlight-content-solid)) {
  font-weight: 700;
  color: #2563eb;
}

/* Dark mode */
:deep(.search-date-picker.vc-is-dark .vc-title) {
  color: #e5e7eb;
}
:deep(.search-date-picker.vc-is-dark .vc-arrow) {
  color: #9ca3af;
}
:deep(.search-date-picker.vc-is-dark .vc-arrow:hover) {
  background-color: #374151;
}
:deep(.search-date-picker.vc-is-dark .vc-weekday) {
  color: #6b7280;
}
:deep(.search-date-picker.vc-is-dark .vc-day-content:hover) {
  background-color: #1e3a5f;
  color: #60a5fa;
}
</style>
